import { Mail01 } from '@untitledui/icons';

import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import { useTitle } from '../hooks/useTitle';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
}

export default function AuthLayout({ children, title }: AuthLayoutProps) {
  useTitle(title);
  const { isDark } = useTheme();

  const illustrationSrc = isDark
    ? '/assets/illustrations/login-illustration-dark.jpg'
    : '/assets/illustrations/login-illustration.jpg';

  return (
    <section className='grid min-h-full lg:min-h-screen grid-cols-1 bg-[var(--color-bg-auth-surface)] dark:bg-gray-900 lg:grid-cols-2'>
      {/* Left Column - Form */}
      <div className='flex flex-col bg-[var(--color-bg-auth-surface)] dark:bg-gray-900'>
        {/* Header with Logo */}
        <header className='p-8'>
          <img
            src='/ahoy-logo-with-text-black.svg'
            alt='Ahoy'
            className='h-8 dark:invert'
          />
        </header>

        {/* Main Content Area */}
        <div className='flex flex-1 items-center justify-center px-4 py-12 md:px-8 md:py-0'>
          <div
            className='flex w-full flex-col gap-8 sm:max-w-90'
            style={{ maxWidth: '360px' }}
          >
            {children}
          </div>
        </div>

        {/* Footer */}
        <footer className='hidden justify-between p-8 pt-11 lg:flex'>
          <p className='text-sm text-gray-500 dark:text-gray-400'>
            © Ahoy Technologies Inc. {new Date().getFullYear()}
          </p>
          <a
            href='mailto:help@ahoy.ai'
            className='flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          >
            <Mail01 className='size-4' />
            help@ahoy.ai
          </a>
        </footer>
      </div>

      {/* Right Column - Illustration (hidden on mobile). The image is zoomed and
          anchored to the right so the artwork fills the column edge-to-edge; a very
          subtle mask softens the left edge into the page background. */}
      <div className='relative hidden h-full flex-1 overflow-hidden bg-[var(--color-bg-auth-surface)] dark:bg-gray-900 lg:block [-webkit-mask-image:linear-gradient(to_right,transparent_0%,#000_5%)] [mask-image:linear-gradient(to_right,transparent_0%,#000_5%)]'>
        <img
          src={illustrationSrc}
          alt=''
          className='absolute inset-0 h-full w-full scale-125 object-cover object-[70%_center]'
        />
      </div>

      {/* Theme Toggle */}
      <div className='absolute top-8 right-8 z-20'>
        <ThemeToggle />
      </div>
    </section>
  );
}
